import type { Event } from '@bugsnag/js';

const CHROME_IOS_USER_AGENT = /\bCriOS\//;
/**
 * Our own code: bundled `.js`/`.mjs` in builds, and `.ts`/`.vue` modules the
 * dev server serves directly.
 */
const SCRIPT_FILE = /\.(?:m?js|ts|vue)(?:[?#]|$)/i;

/**
 * Detect the stack overflow that Chrome on iOS reports from its own injected
 * page scripts.
 *
 * Chrome on iOS injects helper scripts into every page through WebKit user
 * scripts. Their frames carry the HTML document's URL instead of a script
 * file (or no file at all, which Bugsnag shows as an empty or "undefined"
 * name), so they look like an inline script of ours. Creator's own inline
 * scripts are short boot snippets, while these overflows recurse through
 * minified functions and come almost only from Chrome on iOS (99% of the
 * "Maximum call stack size exceeded" group on the overview). They are browser
 * noise we cannot fix, so they are safe to drop.
 *
 * @param event - The {@link Event} Bugsnag is about to report.
 * @returns `true` when the event is this browser noise, so callers can cancel
 *   the report by returning `false` from `onError`.
 */
export default function isChromeIosInlineScriptNoise(event: Event): boolean {
	const userAgent = event.device?.userAgent ?? globalThis.navigator?.userAgent ?? '';

	if (!CHROME_IOS_USER_AGENT.test(userAgent)) {
		return false;
	}

	const [firstError] = event.errors;

	if (
		firstError?.errorClass !== 'RangeError'
		|| !firstError.errorMessage.startsWith('Maximum call stack size exceeded')
	) {
		return false;
	}

	const frames = firstError.stacktrace ?? [];

	return (
		frames.length > 0
		&& frames.every((frame) => !frame.file || !SCRIPT_FILE.test(frame.file))
	);
}
